/**
 * CartButton — icon link with item-count badge that goes to /carrito.
 *
 * Designed to live in any header (landing, /planes, in-app TopBar). Click
 * navigates to the dedicated /carrito page (flujo Culqi). The old slide-over
 * drawer is conservado para acceso desde adentro de la app, pero el botón
 * principal SIEMPRE va a la página completa.
 */

import { Link } from 'react-router-dom';
import { ShoppingCart } from 'lucide-react';
import { useCartStore } from '@/stores/cartStore';

interface CartButtonProps {
  /** "ghost" = transparent pill (default), "filled" = surface-2 background. */
  variant?: 'ghost' | 'filled';
}

export function CartButton({ variant = 'ghost' }: CartButtonProps) {
  const items = useCartStore((s) => s.items);
  const count = items.reduce((acc, it) => acc + it.quantity, 0);

  return (
    <Link
      to="/carrito"
      aria-label={count > 0 ? `Carrito (${count} productos)` : 'Carrito'}
      title={count > 0 ? `${count} productos en el carrito` : 'Tu carrito está vacío'}
      className="relative inline-flex h-10 w-10 items-center justify-center rounded-[var(--radius-pill)] border no-underline transition-colors hover:bg-[var(--surface-2)]"
      style={{
        background: variant === 'filled' ? 'var(--surface-2)' : 'transparent',
        borderColor: 'var(--border-default)',
        color: 'var(--text-primary)',
      }}
    >
      <ShoppingCart size={16} strokeWidth={1.8} />
      {count > 0 && (
        <span
          aria-hidden
          className="absolute -right-1 -top-1 flex h-5 min-w-5 items-center justify-center rounded-full px-1 text-[10px] font-bold"
          style={{
            background: 'var(--accent)',
            color: 'var(--surface-base)',
            boxShadow: '0 0 0 2px var(--surface-1)',
          }}
        >
          {count > 99 ? '99+' : count}
        </span>
      )}
    </Link>
  );
}
